import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const Loading: React.FC = () => {
  const { t } = useTranslation();
  const particlesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Create additional particles dynamically
    const interval = setInterval(() => {
      if (particlesContainerRef.current) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDuration = (Math.random() * 3 + 2) + 's';
        const colors = ['#4f46e5', '#7c3aed', '#2563eb', '#0ea5e9'];
        particle.style.background = colors[Math.floor(Math.random() * colors.length)];
        particlesContainerRef.current.appendChild(particle);
        
        setTimeout(() => {
          if (particlesContainerRef.current && particle.parentNode === particlesContainerRef.current) {
            particlesContainerRef.current.removeChild(particle);
          }
        }, 4000);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative text-center animate-fadeInScale bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 min-h-screen flex items-center justify-center font-['Inter'] overflow-hidden">
      {/* Orbital circles */}
      <div className="absolute w-[300px] h-[300px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-[1]">
        <div className="absolute border border-indigo-500/20 rounded-full animate-spin w-[200px] h-[200px] top-[50px] left-[50px]">
          <div className="absolute w-1.5 h-1.5 bg-indigo-500 rounded-full -top-1.5 left-1/2 transform -translate-x-1/2 shadow-[0_0_10px_#4f46e5]"></div>
        </div>
        <div className="absolute border border-indigo-500/20 rounded-full animate-reverse-spin w-[250px] h-[250px] top-[25px] left-[25px]">
          <div className="absolute w-1.5 h-1.5 bg-indigo-500 rounded-full -top-1.5 left-1/2 transform -translate-x-1/2 shadow-[0_0_10px_#4f46e5]"></div>
        </div>
        <div className="absolute border border-indigo-500/20 rounded-full animate-slow-spin w-[300px] h-[300px] top-0 left-0">
          <div className="absolute w-1.5 h-1.5 bg-indigo-500 rounded-full -top-1.5 left-1/2 transform -translate-x-1/2 shadow-[0_0_10px_#4f46e5]"></div>
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute w-full h-full pointer-events-none z-[2]" ref={particlesContainerRef}>
        <div className="absolute w-1 h-1 bg-indigo-500 rounded-full opacity-0 animate-particleFloat left-[10%]"></div>
        <div className="absolute w-1 h-1 bg-purple-500 rounded-full opacity-0 animate-particleFloat left-[20%] animate-delay-500"></div>
        <div className="absolute w-1 h-1 bg-blue-500 rounded-full opacity-0 animate-particleFloat left-[30%] animate-delay-1000"></div>
        <div className="absolute w-1 h-1 bg-cyan-500 rounded-full opacity-0 animate-particleFloat left-[40%] animate-delay-[1.5s]"></div>
        <div className="absolute w-1 h-1 bg-indigo-500 rounded-full opacity-0 animate-particleFloat left-[50%] animate-delay-2000"></div>
        <div className="absolute w-1 h-1 bg-purple-500 rounded-full opacity-0 animate-particleFloat left-[60%] animate-delay-[2.5s]"></div>
        <div className="absolute w-1 h-1 bg-blue-500 rounded-full opacity-0 animate-particleFloat left-[70%] animate-delay-3000"></div>
        <div className="absolute w-1 h-1 bg-cyan-500 rounded-full opacity-0 animate-particleFloat left-[80%] animate-delay-[3.5s]"></div>
      </div>

      {/* Main content container */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center">
        {/* Main logo */}
        <div className="relative mb-8 animate-logoFloat">
          <div className="text-[120px] font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 bg-[length:300%_300%] bg-clip-text text-transparent animate-gradientShift relative inline-block before:content-[''] before:absolute before:-top-2.5 before:-left-2.5 before:-right-2.5 before:-bottom-2.5 before:bg-gradient-to-r before:from-indigo-500 before:via-purple-500 before:to-blue-500 before:bg-[length:300%_300%] before:rounded-[20px] before:opacity-10 before:animate-gradientShift before:-z-10 before:blur-[15px]">
            CV
          </div>
          <div className="text-[32px] font-normal text-slate-400 -mt-2.5 tracking-[8px] animate-textGlow">
            Match
          </div>
          <div className="text-base font-light text-slate-500 tracking-[6px] mt-1.5 opacity-80">
            S W I S S
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-[300px] h-1 bg-slate-400/20 rounded-sm overflow-hidden my-10 mx-auto relative">
          <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 bg-[length:200%_100%] rounded-sm animate-progressFill origin-left"></div>
          <div className="absolute -top-0.5 left-0 w-full h-2 bg-gradient-to-r from-transparent via-indigo-500 to-transparent rounded opacity-60 animate-glowMove"></div>
        </div>

        {/* Loading text */}
        <div className="text-slate-400 text-sm font-light tracking-[2px] mt-5 animate-pulse">
          {t('app.loading')}
        </div>
      </div>
    </div>
  );
};

export default Loading;